import React from 'react';
import axios from 'axios';
import { format } from 'date-fns';

export const META_INSIGHTS_COLUMNS = [
    { key: 'date', label: 'Date', type: 'date' },
    { key: 'account_id', label: 'Account Id', type: 'string' },
    { key: 'campaign_id', label: 'Campaign ID', type: 'string' },
    { key: 'adset_id', label: 'Ad Set ID', type: 'string' },
    { key: 'ad_id', label: 'Ad ID', type: 'string' },
    { key: 'spend', label: 'Spend', type: 'currency', align: 'right' },
    { key: 'currency', label: 'Currency', type: 'string' },
    { key: 'impressions', label: 'Impressions', type: 'number', align: 'right' },
    { key: 'reach', label: 'Reach', type: 'number', align: 'right' },
    { key: 'clicks', label: 'Clicks', type: 'number', align: 'right' },
    { key: 'link_clicks', label: 'Link Clicks', type: 'number', align: 'right' },
    { key: 'purchases_7dc1dv', label: 'Purchases (7dc1dv)', type: 'number', align: 'right' },
    { key: 'purchase_value_7dc1dv', label: 'Purchase Value (7dc1dv)', type: 'currency', align: 'right' },
];

export const META_ADS_COLUMNS = [
    { key: 'ad_id', label: 'Ad ID', type: 'string', sticky: true },
    { key: 'account_id', label: 'Account Id', type: 'string' },
    { key: 'campaign_id', label: 'Campaign ID', type: 'string' },
    { key: 'adset_id', label: 'Ad Set ID', type: 'string' },
    { key: 'ad_name', label: 'Ad Name', type: 'string' },
    { key: 'adset_name', label: 'Ad Set Name', type: 'string' },
    { key: 'campaign_name', label: 'Campaign Name', type: 'string' },
    { key: 'status', label: 'Status', type: 'status' },
    { key: 'effective_status', label: 'Effective Status', type: 'status' },
    { key: 'destination_url', label: 'Destination URL', type: 'url' },
    { key: 'fetched_at', label: 'Fetched At', type: 'datetime' },
];

export function formatMetaCurrency(value, currency = 'USD') {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

export function formatMetaNumber(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '-';
    return new Intl.NumberFormat('en-US').format(amount);
}

export function formatMetaDateTime(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return format(date, 'MMM d, yyyy h:mm a');
}

export function renderMetaCell(column, row) {
    const value = row?.[column.key];

    switch (column.type) {
        case 'currency':
            return formatMetaCurrency(value, row?.currency);
        case 'number':
            return formatMetaNumber(value);
        case 'datetime':
            return formatMetaDateTime(value);
        case 'date':
            return value || '-';
        case 'status':
            return value || '-';
        case 'url':
            if (!value) return '-';
            return (
                <a
                    href={value}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline break-all"
                >
                    {value}
                </a>
            );
        default:
            return value ?? '-';
    }
}

const EXPORT_PAGE_SIZE = 200;

export async function fetchAllMetaReportData({
    tab,
    selectedAccountIds,
    search,
    totalRecords,
    startDate,
    endDate,
}) {
    if (!selectedAccountIds.length || !totalRecords) {
        return [];
    }

    const exportPages = Math.ceil(totalRecords / EXPORT_PAGE_SIZE);
    const allItems = [];

    for (let page = 1; page <= exportPages; page += 1) {
        const { data: response } = await axios.get('/api/v1/meta/data', {
            params: {
                tab,
                page,
                limit: EXPORT_PAGE_SIZE,
                search: search || undefined,
                accountIds: selectedAccountIds.join(','),
                ...(tab === 'insights' ? { startDate, endDate } : {}),
            },
        });

        allItems.push(...(response?.items || []));
    }

    return allItems;
}
